import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FormDocument, FormElement } from './types';
import { nextElementId } from '../elementRegistry';
import { computeAutoFormattedPages } from '../layout/autoFormatLayout';

export function createBlankDocument(): FormDocument {
  return {
    id: null,
    templateId: null,
    name: '',
    schemaVersion: 1,
    pageSize: 'A4',
    orientation: 'portrait',
    margins: { top: 20, right: 20, bottom: 20, left: 20 },
    pages: [{ id: `page_${Date.now().toString(36)}`, elements: [] }],
  };
}

const initialState: FormDocument = createBlankDocument();

function findPage(doc: FormDocument, pageId: string) {
  return doc.pages.find((p) => p.id === pageId);
}

const formDocumentSlice = createSlice({
  name: 'formDocument',
  initialState,
  reducers: {
    hydrate: (_state, action: PayloadAction<FormDocument>) => action.payload,

    setDocumentMeta: (
      state,
      action: PayloadAction<Partial<Pick<FormDocument, 'name' | 'pageSize' | 'orientation' | 'margins'>>>
    ) => {
      Object.assign(state, action.payload);
    },

    addPage: (state) => {
      state.pages.push({ id: `page_${Date.now().toString(36)}`, elements: [] });
    },

    addElement: (state, action: PayloadAction<{ pageId: string; element: FormElement }>) => {
      const page = findPage(state, action.payload.pageId);
      if (!page) return;
      const maxZ = page.elements.reduce((m, e) => Math.max(m, e.zIndex), 0);
      page.elements.push({ ...action.payload.element, zIndex: maxZ + 1 });
    },

    updateElement: (
      state,
      action: PayloadAction<{ pageId: string; id: string; changes: Partial<FormElement> }>
    ) => {
      const page = findPage(state, action.payload.pageId);
      if (!page) return;
      const el = page.elements.find((e) => e.id === action.payload.id);
      if (!el) return;
      Object.assign(el, action.payload.changes);
    },

    updateElements: (
      state,
      action: PayloadAction<{ pageId: string; changes: { id: string; changes: Partial<FormElement> }[] }>
    ) => {
      const page = findPage(state, action.payload.pageId);
      if (!page) return;
      for (const { id, changes } of action.payload.changes) {
        const el = page.elements.find((e) => e.id === id);
        if (el) Object.assign(el, changes);
      }
    },

    applyLayoutChanges: (
      state,
      action: PayloadAction<{ pageId: string; changes: { id: string; changes: Partial<FormElement> }[] }[]>
    ) => {
      for (const { pageId, changes } of action.payload) {
        const page = findPage(state, pageId);
        if (!page) continue;
        for (const { id, changes: c } of changes) {
          const el = page.elements.find((e) => e.id === id);
          if (el) Object.assign(el, c);
        }
      }
    },

    autoFormatDocument: (state) => {
      state.pages = computeAutoFormattedPages(state);
    },

    removeElements: (state, action: PayloadAction<{ pageId: string; ids: string[] }>) => {
      const page = findPage(state, action.payload.pageId);
      if (!page) return;
      const idSet = new Set(action.payload.ids);
      page.elements = page.elements.filter((e) => !idSet.has(e.id));
    },

    duplicateElements: (state, action: PayloadAction<{ pageId: string; ids: string[]; offset?: number }>) => {
      const page = findPage(state, action.payload.pageId);
      if (!page) return;
      const offset = action.payload.offset ?? 16;
      const maxZ = page.elements.reduce((m, e) => Math.max(m, e.zIndex), 0);
      const idSet = new Set(action.payload.ids);
      const clones = page.elements
        .filter((e) => idSet.has(e.id))
        .map((e, i) => ({
          ...e,
          id: nextElementId(),
          x: e.x + offset,
          y: e.y + offset,
          zIndex: maxZ + 1 + i,
        }));
      page.elements.push(...clones);
    },

    reorderElement: (
      state,
      action: PayloadAction<{ pageId: string; id: string; direction: 'front' | 'back' | 'forward' | 'backward' }>
    ) => {
      const page = findPage(state, action.payload.pageId);
      if (!page) return;
      const sorted = [...page.elements].sort((a, b) => a.zIndex - b.zIndex);
      const idx = sorted.findIndex((e) => e.id === action.payload.id);
      if (idx === -1) return;
      const [el] = sorted.splice(idx, 1);
      switch (action.payload.direction) {
        case 'front':
          sorted.push(el);
          break;
        case 'back':
          sorted.unshift(el);
          break;
        case 'forward':
          sorted.splice(Math.min(idx + 1, sorted.length), 0, el);
          break;
        case 'backward':
          sorted.splice(Math.max(idx - 1, 0), 0, el);
          break;
      }
      sorted.forEach((e, i) => {
        e.zIndex = i + 1;
      });
      page.elements = sorted;
    },

    groupElements: (state, action: PayloadAction<{ pageId: string; ids: string[] }>) => {
      const page = findPage(state, action.payload.pageId);
      if (!page) return;
      const groupId = `grp_${Date.now().toString(36)}`;
      const idSet = new Set(action.payload.ids);
      page.elements.forEach((e) => {
        if (idSet.has(e.id)) e.groupId = groupId;
      });
    },

    ungroupElements: (state, action: PayloadAction<{ pageId: string; groupId: string }>) => {
      const page = findPage(state, action.payload.pageId);
      if (!page) return;
      page.elements.forEach((e) => {
        if (e.groupId === action.payload.groupId) e.groupId = null;
      });
    },
  },
});

export const {
  hydrate,
  setDocumentMeta,
  addPage,
  addElement,
  updateElement,
  updateElements,
  applyLayoutChanges,
  autoFormatDocument,
  removeElements,
  duplicateElements,
  reorderElement,
  groupElements,
  ungroupElements,
} = formDocumentSlice.actions;

export default formDocumentSlice.reducer;
